import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faMagnifyingGlass,
  faStore,
  faUser,
  faKey,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { getPageTitle } from "./NavLink";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import { useAuth } from "../../../hooks";

const TopHeader = ({ onMenuToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const { user, userAccount, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayName = userAccount?.full_name || user?.full_name || "Shop Owner";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const menuItems = [
    {
      label: "My profile",
      icon: faUser,
      onClick: () => navigate("/shop/profile"),
    },
    {
      label: "Change password",
      icon: faKey,
      onClick: () => navigate("/shop/changePassword"),
    },
    { divider: true },
    {
      label: "Logout",
      icon: faRightFromBracket,
      onClick: () => logout(),
      isDanger: true,
    },
  ];

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6">
      {/* LEFT */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuToggle}
        >
          <FontAwesomeIcon icon={faBars} className="h-5 w-5 text-gray-700" />
        </Button>

        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faStore} className="h-5 w-5 text-blue-600" />
          <span className="hidden sm:inline text-lg font-bold text-gray-800">
            Shop Kiganjani
          </span>
        </div>

        <span className="hidden md:inline text-gray-500">/</span>
        <h1 className="hidden md:inline text-sm font-medium text-gray-600">
          {pageTitle}
        </h1>
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-2">
        <div className="relative hidden md:block">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
          />
          <Input placeholder="Search..." className="w-64 pl-8" />
        </div>

        {/* Profile Menu Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={isDropdownOpen}
            className="flex items-center gap-2 rounded-full p-0.5 pr-2 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden sm:inline text-sm font-medium text-gray-700 max-w-[120px] truncate">
              {displayName}
            </span>
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
              <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-600">
                      {user?.role || "Shop Owner"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="py-1">
                {menuItems.map((item, index) => {
                  if (item.divider) {
                    return (
                      <div
                        key={index}
                        className="border-t border-gray-100 my-1"
                      />
                    );
                  }
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setIsDropdownOpen(false);
                        item.onClick();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        item.isDanger
                          ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                          : "text-gray-700 hover:bg-blue-50 hover:text-blue-900"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={`h-4 w-4 ${
                          item.isDanger ? "text-red-600" : "text-gray-500"
                        }`}
                      />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
